import React, { useState, useEffect, useCallback } from "react";
import Layout from "../components/Layout";
import { auditoriaAPI } from "../services/api";
import {
  ShieldAlert, RefreshCw, Search, Filter, Users,
  Activity, AlertTriangle, CheckCircle, Clock, TrendingUp,
} from "lucide-react";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip,
  ResponsiveContainer, CartesianGrid,
} from "recharts";

// ─── Tipos ────────────────────────────────────────────────────────────────────
interface RegistroAuditoria {
  id: string;
  user_nombre: string | null;
  user_email: string | null;
  user_rol: string | null;
  academia_id: string | null;
  metodo: string;
  endpoint: string;
  modulo: string | null;
  accion: string | null;
  ip_address: string | null;
  status_code: number | null;
  duracion_ms: number | null;
  created_at: string;
}
interface Resumen {
  total_eventos: number;
  errores_periodo: number;
  por_modulo: { modulo: string; count: number }[];
  top_usuarios: { nombre: string; email: string; rol: string; count: number }[];
  por_dia: { dia: string; count: number }[];
  ultimos_logins: RegistroAuditoria[];
}
interface UsuarioActivo {
  user_id: string;
  nombre: string;
  email: string;
  rol: string;
  total_acciones: number;
  ultimo_acceso: string;
  primer_acceso: string;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
const METHOD_COLORS: Record<string, string> = {
  GET: "bg-gray-100 text-gray-700",
  POST: "bg-green-100 text-green-700",
  PUT: "bg-blue-100 text-blue-700",
  PATCH: "bg-blue-100 text-blue-700",
  DELETE: "bg-red-100 text-red-700",
};

const ROL_LABELS: Record<string, string> = {
  admin: "Admin", dt: "DT", pf: "Prep.Físico",
  fisio: "Fisio", nutricion: "Nutri", psicologo: "Psicólogo",
  analista: "Analista", jugador: "Jugador", padre: "Padre",
};

function fmt(dt: string): string {
  try {
    const d = new Date(dt);
    return d.toLocaleString("es-AR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit" });
  } catch { return dt; }
}

function statusBadge(code: number | null) {
  if (!code) return <span className="text-gray-400">—</span>;
  const cls = code < 300 ? "text-green-600" : code < 500 ? "text-yellow-600" : "text-red-600";
  return <span className={`font-mono text-xs font-bold ${cls}`}>{code}</span>;
}

function iniciales(nombre: string | null): string {
  if (!nombre) return "?";
  const parts = nombre.trim().split(" ");
  return (parts[0][0] + (parts[1]?.[0] || "")).toUpperCase();
}

const MODULOS = [
  "", "auth", "jugadores", "categorias", "torneos", "staff", "usuarios",
  "wellness", "pagos", "partidos", "analytics", "mediciones", "notas",
  "export", "padres", "stats", "notificaciones",
];

// ─── Componentes de sección ────────────────────────────────────────────────────
function StatCard({ icon, label, value, sub, color }: { icon: React.ReactNode; label: string; value: string | number; sub?: string; color: string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>{icon}</div>
      <div>
        <div className="text-2xl font-bold text-gray-900">{value}</div>
        <div className="text-sm font-medium text-gray-600">{label}</div>
        {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
      </div>
    </div>
  );
}

// ─── Tab: Resumen ──────────────────────────────────────────────────────────────
function TabResumen({ dias, onDiasChange }: { dias: number; onDiasChange: (d: number) => void }) {
  const [data, setData] = useState<Resumen | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    auditoriaAPI.resumen(dias)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="flex justify-center py-20"><RefreshCw className="animate-spin text-blue-500" size={28}/></div>;
  if (!data) return null;

  const tasa_error = data.total_eventos > 0 ? ((data.errores_periodo / data.total_eventos) * 100).toFixed(1) : "0";

  return (
    <div className="space-y-6">
      {/* Selector de período */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide">Período de análisis</h3>
        <div className="flex gap-2">
          {[7, 14, 30, 90].map(d => (
            <button key={d} onClick={() => onDiasChange(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dias === d ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-blue-300"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={<Activity size={22} className="text-blue-600"/>} label="Total eventos" value={data.total_eventos.toLocaleString()} color="bg-blue-50" />
        <StatCard icon={<AlertTriangle size={22} className="text-red-500"/>} label="Errores (4xx/5xx)" value={data.errores_periodo} sub={`${tasa_error}% del total`} color="bg-red-50" />
        <StatCard icon={<Users size={22} className="text-purple-600"/>} label="Usuarios activos" value={data.top_usuarios.length} sub={`últimos ${dias} días`} color="bg-purple-50" />
        <StatCard icon={<CheckCircle size={22} className="text-green-600"/>} label="Módulos con actividad" value={data.por_modulo.length} color="bg-green-50" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Eventos por día */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <TrendingUp size={16} className="text-blue-500"/> Actividad por día
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <LineChart data={data.por_dia} margin={{ top: 2, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="dia" tick={{ fontSize: 11 }} tickFormatter={d => d.slice(5)}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip formatter={(v: any) => [v, "Acciones"]} labelFormatter={l => `Día: ${l}`}/>
              <Line type="monotone" dataKey="count" stroke="#3b82f6" strokeWidth={2} dot={false}/>
            </LineChart>
          </ResponsiveContainer>
        </div>

        {/* Por módulo */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-4 flex items-center gap-2">
            <Filter size={16} className="text-purple-500"/> Acciones por módulo
          </h4>
          <ResponsiveContainer width="100%" height={180}>
            <BarChart data={data.por_modulo.slice(0, 10)} margin={{ top: 2, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0"/>
              <XAxis dataKey="modulo" tick={{ fontSize: 10 }}/>
              <YAxis tick={{ fontSize: 11 }}/>
              <Tooltip/>
              <Bar dataKey="count" fill="#8b5cf6" radius={[4, 4, 0, 0]}/>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top usuarios + últimos logins */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Top usuarios más activos</h4>
          <div className="space-y-2">
            {data.top_usuarios.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Sin datos</p>}
            {data.top_usuarios.map((u, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {iniciales(u.nombre)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium text-gray-800 truncate">{u.nombre || u.email}</div>
                  <div className="text-xs text-gray-400">{ROL_LABELS[u.rol] || u.rol}</div>
                </div>
                <span className="text-sm font-bold text-blue-600">{u.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h4 className="text-sm font-semibold text-gray-700 mb-3">Últimos inicios de sesión</h4>
          <div className="space-y-2">
            {data.ultimos_logins.length === 0 && <p className="text-sm text-gray-400 py-4 text-center">Sin datos</p>}
            {data.ultimos_logins.slice(0, 8).map((r, i) => (
              <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-green-400 to-teal-500 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  {iniciales(r.user_nombre)}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-medium text-gray-800 truncate">{r.user_nombre || r.user_email}</div>
                  <div className="text-xs text-gray-400">{r.ip_address}</div>
                </div>
                <div className="text-xs text-gray-400 text-right">{fmt(r.created_at)}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Actividad en vivo ────────────────────────────────────────────────────
function TabActividad() {
  const [registros, setRegistros] = useState<RegistroAuditoria[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [offset, setOffset] = useState(0);
  const LIMITE = 50;

  // Filtros
  const [buscar, setBuscar] = useState("");
  const [modulo, setModulo] = useState("");
  const [metodo, setMetodo] = useState("");
  const [rolFiltro, setRolFiltro] = useState("");
  const [fechaDesde, setFechaDesde] = useState("");
  const [fechaHasta, setFechaHasta] = useState("");
  const [soloErrores, setSoloErrores] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(new Date());
  const [autoRefresh, setAutoRefresh] = useState(true);

  const load = useCallback((off = 0) => {
    setLoading(true);
    const params: any = { limite: LIMITE, offset: off };
    if (buscar) params.buscar = buscar;
    if (modulo) params.modulo = modulo;
    if (metodo) params.metodo = metodo;
    if (rolFiltro) params.user_rol = rolFiltro;
    if (fechaDesde) params.fecha_desde = fechaDesde;
    if (fechaHasta) params.fecha_hasta = fechaHasta;
    if (soloErrores) params.status_code = 400;

    auditoriaAPI.listar(params)
      .then(r => {
        setRegistros(r.data.registros || []);
        setTotal(r.data.total || 0);
        setLastUpdated(new Date());
      })
      .finally(() => setLoading(false));
  }, [buscar, modulo, metodo, rolFiltro, fechaDesde, fechaHasta, soloErrores]);

  useEffect(() => { setOffset(0); load(0); }, [buscar, modulo, metodo, rolFiltro, fechaDesde, fechaHasta, soloErrores]);
  useEffect(() => { if (offset > 0) load(offset); }, [offset]);

  // Auto-refresh cada 30s
  useEffect(() => {
    if (!autoRefresh) return;
    const t = setInterval(() => load(offset), 30000);
    return () => clearInterval(t);
  }, [autoRefresh, load, offset]);

  const secsAgo = Math.round((new Date().getTime() - lastUpdated.getTime()) / 1000);

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
          <div className="relative">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"/>
            <input value={buscar} onChange={e => setBuscar(e.target.value)} placeholder="Buscar usuario, IP..."
              className="w-full pl-8 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          </div>
          <select value={modulo} onChange={e => setModulo(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los módulos</option>
            {MODULOS.filter(m => m).map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={metodo} onChange={e => setMetodo(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los métodos</option>
            {["GET","POST","PUT","PATCH","DELETE"].map(m => <option key={m} value={m}>{m}</option>)}
          </select>
          <select value={rolFiltro} onChange={e => setRolFiltro(e.target.value)}
            className="py-2 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500">
            <option value="">Todos los roles</option>
            {Object.entries(ROL_LABELS).map(([k,v]) => <option key={k} value={k}>{v}</option>)}
          </select>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <input type="date" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)}
            className="py-1.5 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          <span className="text-gray-400 text-sm">a</span>
          <input type="date" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)}
            className="py-1.5 px-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"/>
          <label className="flex items-center gap-1.5 text-sm text-gray-600 cursor-pointer">
            <input type="checkbox" checked={soloErrores} onChange={e => setSoloErrores(e.target.checked)} className="rounded"/>
            Solo errores
          </label>
          <div className="ml-auto flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs text-gray-500 cursor-pointer">
              <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} className="rounded"/>
              Auto (30s)
            </label>
            <span className="text-xs text-gray-400">· actualizado hace {secsAgo}s</span>
            <button onClick={() => load(offset)} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
              <RefreshCw size={14} className={`text-gray-500 ${loading ? "animate-spin" : ""}`}/>
            </button>
          </div>
        </div>
      </div>

      {/* Tabla */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">{total.toLocaleString()} registros encontrados</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Fecha / Hora</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acción</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Método</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">IP</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">ms</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {loading && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">
                  <RefreshCw className="animate-spin inline mr-2" size={16}/>Cargando...
                </td></tr>
              )}
              {!loading && registros.length === 0 && (
                <tr><td colSpan={7} className="text-center py-12 text-gray-400">Sin registros para los filtros actuales</td></tr>
              )}
              {!loading && registros.map(r => (
                <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap font-mono">{fmt(r.created_at)}</td>
                  <td className="px-4 py-3">
                    {r.user_nombre ? (
                      <div className="flex items-center gap-2">
                        <div className="w-7 h-7 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                          {iniciales(r.user_nombre)}
                        </div>
                        <div className="min-w-0">
                          <div className="text-xs font-medium text-gray-800 truncate max-w-[120px]">{r.user_nombre}</div>
                          <div className="text-xs text-gray-400">{ROL_LABELS[r.user_rol || ""] || r.user_rol}</div>
                        </div>
                      </div>
                    ) : (
                      <span className="text-gray-400 text-xs italic">Anónimo</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-xs font-medium text-gray-800">{r.accion || r.modulo || "—"}</div>
                    <div className="text-xs text-gray-400 truncate max-w-[200px]">{r.endpoint}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${METHOD_COLORS[r.metodo] || "bg-gray-100 text-gray-600"}`}>
                      {r.metodo}
                    </span>
                  </td>
                  <td className="px-4 py-3">{statusBadge(r.status_code)}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{r.ip_address || "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{r.duracion_ms ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {/* Paginación */}
        {total > LIMITE && (
          <div className="px-5 py-3 border-t border-gray-100 flex items-center justify-between">
            <span className="text-xs text-gray-500">
              Mostrando {offset + 1}–{Math.min(offset + LIMITE, total)} de {total.toLocaleString()}
            </span>
            <div className="flex gap-2">
              <button disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMITE))}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                ← Anterior
              </button>
              <button disabled={offset + LIMITE >= total} onClick={() => setOffset(offset + LIMITE)}
                className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg disabled:opacity-40 hover:bg-gray-50">
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Tab: Usuarios activos ─────────────────────────────────────────────────────
function TabUsuariosActivos() {
  const [usuarios, setUsuarios] = useState<UsuarioActivo[]>([]);
  const [loading, setLoading] = useState(true);
  const [dias, setDias] = useState(30);

  const load = useCallback(() => {
    setLoading(true);
    auditoriaAPI.usuariosActivos(dias)
      .then(r => setUsuarios(r.data))
      .finally(() => setLoading(false));
  }, [dias]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">Usuarios que han accedido al sistema en los últimos:</p>
        <div className="flex gap-2">
          {[7, 14, 30, 60, 90].map(d => (
            <button key={d} onClick={() => setDias(d)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-all ${dias === d ? "bg-blue-600 text-white" : "bg-white border border-gray-200 text-gray-600 hover:border-blue-300"}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center py-16"><RefreshCw className="animate-spin text-blue-500" size={24}/></div>
        ) : usuarios.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Users size={40} className="mx-auto mb-3 opacity-30"/>
            <p>Sin actividad en los últimos {dias} días</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 text-left">
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Total acciones</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Primer acceso</th>
                <th className="px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Último acceso</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {usuarios.map((u, i) => (
                <tr key={i} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-white text-xs font-bold">
                        {iniciales(u.nombre)}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">{u.nombre || "—"}</div>
                        <div className="text-xs text-gray-400">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-0.5 bg-indigo-50 text-indigo-700 rounded-full text-xs font-medium">
                      {ROL_LABELS[u.rol] || u.rol}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center font-bold text-blue-600">{u.total_acciones.toLocaleString()}</td>
                  <td className="px-4 py-3 text-xs text-gray-400 font-mono">{u.primer_acceso ? fmt(u.primer_acceso) : "—"}</td>
                  <td className="px-4 py-3 text-xs text-gray-500 font-mono">{u.ultimo_acceso ? fmt(u.ultimo_acceso) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// ─── Página principal ──────────────────────────────────────────────────────────
type Tab = "resumen" | "actividad" | "usuarios";

export default function AuditoriaPage() {
  const [tab, setTab] = useState<Tab>("resumen");
  const [dias, setDias] = useState(7);

  const TABS: { key: Tab; label: string; icon: React.ReactNode }[] = [
    { key: "resumen",   label: "Resumen",          icon: <TrendingUp size={15}/> },
    { key: "actividad", label: "Actividad en vivo", icon: <Activity size={15}/> },
    { key: "usuarios",  label: "Usuarios activos",  icon: <Users size={15}/> },
  ];

  return (
    <Layout>
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center">
            <ShieldAlert size={20} className="text-white"/>
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Auditoría del Sistema</h1>
            <p className="text-sm text-gray-500">Registro completo de actividad — todo queda registrado</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                tab === t.key ? "bg-white text-gray-900 shadow-sm" : "text-gray-500 hover:text-gray-700"
              }`}>
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        {/* Contenido */}
        {tab === "resumen"   && <TabResumen dias={dias} onDiasChange={setDias}/>}
        {tab === "actividad" && <TabActividad/>}
        {tab === "usuarios"  && <TabUsuariosActivos/>}
      </div>
    </Layout>
  );
}
